import { useState, useEffect, useCallback } from 'react';
import { ArrowUp, ArrowDown, ArrowLeft, ArrowRight } from '@phosphor-icons/react';
import { Button } from '../ui/button';

const SIZE = 4;
const empty = () => Array(SIZE * SIZE).fill(0);

const spawn = (grid) => {
  const free = grid.map((v, i) => (v === 0 ? i : -1)).filter((i) => i >= 0);
  if (free.length === 0) return grid;
  const next = [...grid];
  next[free[Math.floor(Math.random() * free.length)]] = Math.random() < 0.9 ? 2 : 4;
  return next;
};

const rows = (grid) => Array.from({ length: SIZE }, (_, r) => grid.slice(r * SIZE, r * SIZE + SIZE));
const flatten = (matrix) => matrix.flat();
const transpose = (matrix) => matrix[0].map((_, c) => matrix.map((row) => row[c]));

const collapse = (row) => {
  const values = row.filter((v) => v !== 0);
  const out = [];
  let gained = 0;
  for (let i = 0; i < values.length; i++) {
    if (values[i] === values[i + 1]) {
      out.push(values[i] * 2);
      gained += values[i] * 2;
      i++;
    } else {
      out.push(values[i]);
    }
  }
  while (out.length < SIZE) out.push(0);
  return { row: out, gained };
};

const move = (grid, direction) => {
  let matrix = rows(grid);
  if (direction === 'up' || direction === 'down') matrix = transpose(matrix);
  let gained = 0;
  matrix = matrix.map((row) => {
    const source = direction === 'right' || direction === 'down' ? [...row].reverse() : row;
    const { row: collapsed, gained: g } = collapse(source);
    gained += g;
    return direction === 'right' || direction === 'down' ? collapsed.reverse() : collapsed;
  });
  if (direction === 'up' || direction === 'down') matrix = transpose(matrix);
  return { grid: flatten(matrix), gained };
};

const canMove = (grid) => ['up', 'down', 'left', 'right'].some((d) => move(grid, d).grid.some((v, i) => v !== grid[i]));

const TILE_STYLES = {
  0: 'bg-white/5 text-transparent',
  2: 'bg-amber-100/20 text-amber-100',
  4: 'bg-amber-200/25 text-amber-100',
  8: 'bg-amber-400/30 text-amber-200',
  16: 'bg-amber-500/35 text-amber-100',
  32: 'bg-orange-500/40 text-white',
  64: 'bg-orange-600/50 text-white',
  128: 'bg-rose-500/50 text-white',
  256: 'bg-rose-600/60 text-white',
  512: 'bg-fuchsia-600/60 text-white',
  1024: 'bg-violet-600/70 text-white',
  2048: 'bg-emerald-500/80 text-white'
};

export function Game2048({ onScore }) {
  const [grid, setGrid] = useState(() => spawn(spawn(empty())));
  const [score, setScore] = useState(0);
  const [over, setOver] = useState(false);

  const push = useCallback((direction) => {
    setGrid((current) => {
      if (over) return current;
      const { grid: moved, gained } = move(current, direction);
      if (moved.every((v, i) => v === current[i])) return current;
      const next = spawn(moved);
      setScore((s) => s + gained);
      if (!canMove(next)) setOver(true);
      return next;
    });
  }, [over]);

  useEffect(() => {
    const keys = { ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right' };
    const handler = (e) => {
      if (keys[e.key]) {
        e.preventDefault();
        push(keys[e.key]);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [push]);

  useEffect(() => {
    if (over && score > 0) onScore(score);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [over]);

  const restart = () => {
    setGrid(spawn(spawn(empty())));
    setScore(0);
    setOver(false);
  };

  return (
    <div className="space-y-4" data-testid="game-2048">
      <div className="flex items-center justify-between text-sm">
        <span>Score: <span className="text-amber-300 tabular-nums" data-testid="g2048-score">{score}</span></span>
        <span className="text-muted-foreground text-xs">Arrow keys or buttons</span>
      </div>

      <div className="grid grid-cols-4 gap-2 p-2 rounded-lg border border-white/10 bg-black/40">
        {grid.map((value, i) => (
          <div
            key={i}
            className={`aspect-square rounded flex items-center justify-center text-lg sm:text-xl font-medium tabular-nums transition-colors ${TILE_STYLES[value] || 'bg-emerald-600 text-white'}`}
            data-testid={`g2048-tile-${i}`}
          >
            {value || '0'}
          </div>
        ))}
      </div>

      {over && <p className="text-center text-sm text-rose-400" data-testid="g2048-over">No moves left. Final score {score}.</p>}

      <div className="flex flex-col items-center gap-2">
        <Button variant="outline" size="sm" className="rounded-full w-12" onClick={() => push('up')} data-testid="g2048-up"><ArrowUp size={16} /></Button>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="rounded-full w-12" onClick={() => push('left')} data-testid="g2048-left"><ArrowLeft size={16} /></Button>
          <Button variant="outline" size="sm" className="rounded-full w-12" onClick={() => push('down')} data-testid="g2048-down"><ArrowDown size={16} /></Button>
          <Button variant="outline" size="sm" className="rounded-full w-12" onClick={() => push('right')} data-testid="g2048-right"><ArrowRight size={16} /></Button>
        </div>
      </div>

      <Button variant="outline" className="rounded-full w-full" onClick={restart} data-testid="g2048-restart">New game</Button>
    </div>
  );
}
